// frontend/src/firebase.js
import { initializeApp } from "firebase/app";
import { getAuth, initializeAuth, indexedDBLocalPersistence } from "firebase/auth";
import { getFirestore, initializeFirestore } from "firebase/firestore";
import { getStorage } from "firebase/storage";
import { Capacitor } from '@capacitor/core';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  databaseURL: `https://${import.meta.env.VITE_FIREBASE_PROJECT_ID}-default-rtdb.asia-southeast1.firebasedatabase.app`,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
};

const app = initializeApp(firebaseConfig);

// ✅ capacitorHTTP 호환: Firebase 초기화 설정
let auth;
let db;
let storage;

try {
  const platform = Capacitor.getPlatform();
  
  if (platform === 'ios') {
    console.log('📱 iOS 플랫폼: capacitorHTTP 호환 설정 적용');
    
    // Auth 초기화
    auth = initializeAuth(app, {
      persistence: indexedDBLocalPersistence
    });
    
    // 🔑 iOS: capacitorHTTP 충돌 해결 설정 (experimentalAutoDetectLongPolling 제거)
    db = initializeFirestore(app, {
      experimentalForceLongPolling: true,
      ignoreUndefinedProperties: true,
      cacheSizeBytes: -1
    });
    
    // Storage 초기화
    storage = getStorage(app);
    
    console.log('✅ iOS Firebase 초기화 완료 (capacitorHTTP 호환 - Long Polling 전용)');
  } else if (platform === 'android') {
    console.log('🤖 Android 플랫폼: 기본 설정 사용 (capacitorHTTP 비활성화)');
    
    // Auth 초기화
    auth = initializeAuth(app, {
      persistence: indexedDBLocalPersistence
    });
    
    // 🔑 Android: 기본 Firestore 설정 (capacitorHTTP 비활성화 상태)
    db = initializeFirestore(app, {
      ignoreUndefinedProperties: true
    });
    
    // Storage 초기화
    storage = getStorage(app);
    
    console.log('✅ Android Firebase 초기화 완료 (기본 설정)');
  } else {
    console.log('🌐 웹 플랫폼: 기본 설정 사용');
    auth = getAuth(app);
    db = getFirestore(app);
    storage = getStorage(app);
  }
} catch (error) {
  console.warn('⚠️ Firebase 이미 초기화됨, 기존 인스턴스 사용:', error.message);
  auth = getAuth(app);
  db = getFirestore(app);
  storage = getStorage(app);
}

// 개발 환경에서 콘솔 디버깅을 위해 전역 객체로 노출
if (import.meta.env.DEV) {
  window.firebase = { auth, db, storage };
  
  // Firestore 유틸리티 함수들을 전역으로 노출
  import('firebase/firestore').then((firestoreModule) => {
    window.firebaseUtils = {
      doc: firestoreModule.doc,
      getDoc: firestoreModule.getDoc,
      updateDoc: firestoreModule.updateDoc,
      setDoc: firestoreModule.setDoc,
      collection: firestoreModule.collection,
      query: firestoreModule.query,
      where: firestoreModule.where,
      orderBy: firestoreModule.orderBy,
      limit: firestoreModule.limit,
      getDocs: firestoreModule.getDocs,
      onSnapshot: firestoreModule.onSnapshot,
      serverTimestamp: firestoreModule.serverTimestamp,
      Timestamp: firestoreModule.Timestamp
    };
    console.log('🔧 Firebase debugging enabled: window.firebase, window.firebaseUtils');
  });
}

export { auth, db, storage };